/**
 * Operations Permission Management Hooks
 * TanStack Query hooks for managing user operations permissions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import {
  fetchUsersWithPermissions,
  updateUserPermissions,
  type OperationsUserWithPermissions,
} from '../api';

// Query keys factory
export const operationsPermissionKeys = {
  all: ['operations-permissions'] as const,
  users: (search?: string) => [...operationsPermissionKeys.all, 'users', search] as const,
};

// Available operations permissions that can be managed
export const OPERATIONS_PERMISSIONS = [
  {
    key: 'operations.tasks.create',
    labelKey: 'settings.permissions.tasks.create',
    descriptionKey: 'settings.permissions.tasks.createDescription',
  },
  {
    key: 'operations.tasks.edit',
    labelKey: 'settings.permissions.tasks.edit',
    descriptionKey: 'settings.permissions.tasks.editDescription',
  },
  {
    key: 'operations.tasks.delete',
    labelKey: 'settings.permissions.tasks.delete',
    descriptionKey: 'settings.permissions.tasks.deleteDescription',
  },
  {
    key: 'operations.tasks.assign',
    labelKey: 'settings.permissions.tasks.assign',
    descriptionKey: 'settings.permissions.tasks.assignDescription',
  },
  {
    key: 'operations.projects.create',
    labelKey: 'settings.permissions.projects.create',
    descriptionKey: 'settings.permissions.projects.createDescription',
  },
  {
    key: 'operations.projects.edit',
    labelKey: 'settings.permissions.projects.edit',
    descriptionKey: 'settings.permissions.projects.editDescription',
  },
  {
    key: 'operations.projects.delete',
    labelKey: 'settings.permissions.projects.delete',
    descriptionKey: 'settings.permissions.projects.deleteDescription',
  },
  {
    key: 'operations.projects.assign',
    labelKey: 'settings.permissions.projects.assign',
    descriptionKey: 'settings.permissions.projects.assignDescription',
  },
] as const;

export type OperationsPermissionKey = (typeof OPERATIONS_PERMISSIONS)[number]['key'];

/**
 * Hook for fetching users with their permissions
 */
export function useUsersWithPermissions(search?: string) {
  return useQuery({
    queryKey: operationsPermissionKeys.users(search),
    queryFn: () => fetchUsersWithPermissions(search),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Mutation variables for updating permissions
 * Includes the specific permission being toggled for UI feedback
 */
export interface UpdatePermissionVariables {
  userId: string;
  permissionStrings: string[];
  permissionKey: string; // The specific permission being toggled
}

/**
 * Hook for updating a user's permissions
 * Automatically invalidates session if current user's permissions are updated
 */
export function useUpdateUserPermissions() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  const { user: currentUser, invalidateSession } = useSession();

  return useMutation({
    mutationFn: ({ userId, permissionStrings }: UpdatePermissionVariables) =>
      updateUserPermissions(userId, permissionStrings),
    onMutate: async ({ userId, permissionStrings }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: operationsPermissionKeys.all });

      // Snapshot the previous value for all user queries
      const previousData = queryClient.getQueriesData<OperationsUserWithPermissions[]>({
        queryKey: operationsPermissionKeys.all,
      });

      // Optimistically update all matching queries
      queryClient.setQueriesData<OperationsUserWithPermissions[]>(
        { queryKey: operationsPermissionKeys.all },
        (old) => {
          if (!old) return old;
          return old.map((user) =>
            user._id === userId
              ? { ...user, permission_strings: permissionStrings }
              : user
          );
        }
      );

      return { previousData };
    },
    onSuccess: (_data, variables) => {
      toast.success(t('settings.permissions.updateSuccess'));

      // If we updated the current user's permissions, invalidate their session
      // so the new permissions take effect immediately
      if (currentUser?._id === variables.userId) {
        invalidateSession();
      }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(t('settings.permissions.updateError'));
    },
    onSettled: () => {
      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: operationsPermissionKeys.all });
    },
  });
}

/**
 * Helper to check if a user has a specific operations permission
 */
export function hasOperationsPermission(
  user: OperationsUserWithPermissions,
  permission: OperationsPermissionKey
): boolean {
  return user.permission_strings.includes(permission);
}

/**
 * Helper to toggle a permission for a user
 * Returns the new permission_strings array
 */
export function togglePermission(
  currentPermissions: string[],
  permission: OperationsPermissionKey,
  enabled: boolean
): string[] {
  if (enabled) {
    // Add permission if not already present
    if (!currentPermissions.includes(permission)) {
      return [...currentPermissions, permission];
    }
    return currentPermissions;
  } else {
    // Remove permission
    return currentPermissions.filter((p) => p !== permission);
  }
}
